"use client";
import React, { useState } from "react";
import s from "./SectionPromo.module.scss";
import { Container } from "components/layout/Container";
import { Breadcrumbs } from "components/shared/Breadcrumbs";
import { BreadcrumbsProps } from "components/shared/Breadcrumbs";
import { WriterCardProps } from "@/components/pages/writing-service/WriterCard";
import { Heading } from "components/UI/typography/Heading";
import { Button } from "components/UI/buttons/Button";
import downloadIcon from "public/img/icons/download.svg";
import printIcon from "public/img/icons/print.svg";
import { SpriteIcon } from "components/UI/SpriteIcon";
import { Portal } from "components/shared/Portal";
import { DownloadSampleModal } from "./DownloadSampleModal";
import { Tag } from "@/components/UI/Tag";
import IconTurnDownRight from "@/public/img/icons/arrow-turn-down-right.svg";
import IconEye from "@/public/img/icons/eye.svg";
import { CategoryList } from "@/components/shared/CategoryList";
import Image from "next/legacy/image";
import { animateScroll } from "@/utils/animateScroll";

type SectionPromoProps = {
  breadcrumbs: BreadcrumbsProps;
  heading: string;
  tags?: string[];
  datePublished: string;
  format: string;
  level: string;
  type: string;
  words: number;
  pages: number;
  views: number;
  downloads: number;
  headingTag?: number;
  category: {
    title: string;
    slug: string;
  };
  subcategory: {
    id: number;
    title: string;
    slug: string;
  };
  writer?: WriterCardProps;
  article: string;
  slug: string;
  topics?: {
    name: string;
    href: string;
  }[];
};

export const SectionPromo = ({
  breadcrumbs,
  heading,
  datePublished,
  format,
  level,
  type,
  words,
  pages,
  views,
  downloads,
  writer,
  category,
  subcategory,
  article,
  headingTag = 1,
  slug,
  topics,
}: SectionPromoProps) => {
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // console.log("article", article);

  const onPrintButtonClick = (event: React.MouseEvent) => {
    const parser = new DOMParser();

    const text = parser.parseFromString(article, "text/html").body.innerHTML;

    const iframe = document.createElement("iframe");
    iframe.id = "print";
    iframe.name = "print";

    iframe.onload = () => {
      // @ts-ignore
      iframe.contentDocument.body.innerHTML = `<h1 style="text-align: center">${heading}</h1>` + text;
      iframe.contentWindow?.focus();

      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 0);
    };

    document.body.appendChild(iframe);
  };

  const handleCiteButtonClick = () => {
    const target = document.getElementById("citations");

    if (!target) return;

    animateScroll(target);
  };

  return (
    <Container>
      <section className={s.section}>
        <Breadcrumbs list={breadcrumbs.list} className={s.breadcrumbs} />
        <div className={s.inner}>
          <div className={s.topMeta}>
            <p title="Date published">{datePublished}</p>
            <p title="Views">
              <SpriteIcon icon={IconEye} /> {views}
            </p>
            <button type="button" className={s.citeButton} onClick={handleCiteButtonClick}>
              <Image src="/img/link.svg" alt="Illustration" width={24} height={24} />
              Cite this page
            </button>
          </div>
          <Heading level={1} tag={headingTag} className={s.heading} html={heading} />
          <ul className={s.tags}>
            <li>
              <Tag link={{ href: `/${category.slug}` }}>{category.title}</Tag>
            </li>
            <li>
              <Tag link={{ href: `/${category.slug}/${subcategory.slug}` }}>
                <SpriteIcon icon={IconTurnDownRight} className={s.subjectIcon} />
                {subcategory.title}
              </Tag>
            </li>
          </ul>
          <div className={s.meta}>
            <div className={s.metaCol}>
              <p>
                Format: <strong>{format}</strong>
              </p>
              <p>
                Academic level: <strong>{level}</strong>
              </p>
              <p>
                Paper type: <strong>{type}</strong>
              </p>
            </div>
            <div className={s.metaCol}>
              <p>
                Words: <strong>{words}</strong>
              </p>
              <p>
                Pages: <strong>{pages}</strong>
              </p>
              <p>
                Downloads: <strong>{downloads}</strong>
              </p>
            </div>
          </div>
          <div className={s.bottom}>
            {!!topics?.length && (
              <CategoryList title="Topics:" wrap categories={topics} className={s.topics} />
            )}
            <div className={s.buttons}>
              <Button
                tag="button"
                type="secondary"
                className={s.btn}
                onClick={() => setDownloadModalOpen(true)}
              >
                <SpriteIcon icon={downloadIcon} />
                Download
              </Button>
              <Button tag="button" type="secondary" className={s.btn} onClick={onPrintButtonClick}>
                <SpriteIcon icon={printIcon} />
                Print
              </Button>
            </div>
          </div>
        </div>
      </section>
      <Portal>
        <DownloadSampleModal slug={slug} open={downloadModalOpen} setOpen={setDownloadModalOpen} />
      </Portal>
    </Container>
  );
};
